import { TyDatePicker, TyOption, TySelectInput } from '@tymbe/ui-forms';
import { FavouriteIcon, ImageIcon } from '@tymbe/ui-icons';
import { FieldApi, useFieldApi } from 'informed';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CompanyDefaultSettingsData } from '@tymbe/schema/company-default-settings.interface';
import { ContestDuration } from '@tymbe/schema/enums';
import toHHMM from '../../../../utils/toHHMM';

export const enum ContestDurationType {
  hoursBeforeAt = 'hoursBeforeAt',
  dayBeforeAt = 'dayBeforeAt',
  workdayBeforeAt = 'workdayBeforeAt',
}

const validateContestEndTime = (value?: string | null, contestEndVariantValue?: ContestDurationType | ContestDuration) => {
  if (contestEndVariantValue && !value) {
    return 'Čas konce zveřejnění je povinný';
  }
  return undefined;
};

type Props = {
  companyDefaultSettings?: CompanyDefaultSettingsData;
}

export const ContestDurationSettings = ({
  companyDefaultSettings
}: Props) => {
  const startDates = useFieldApi('startDates') as FieldApi<MomentInput>;
  const startTime = useFieldApi('startTime') as FieldApi<MomentInput>;
  const contestStart = useFieldApi('contestStart') as FieldApi<string>;
  const contestEndTime = useFieldApi('contestEndTime') as FieldApi<string>;
  const contestEndVariant = useFieldApi('contestEndVariant') as FieldApi<string>;
  const shiftStartDates = startDates.getValue();
  const shiftStartTime = startTime.getValue();

  const sortedDates = Array.isArray(shiftStartDates)
    ? [...shiftStartDates].sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
    : [];

  const [startHours, startMinutes] = shiftStartTime ? shiftStartTime.split(':').map(Number) : [0, 0];

  const startDateTime = moment(sortedDates[0]).set({
    hour: startHours,
    minute: startMinutes,
  });

  const [contestEndVariantValue, setContestEndVariantValue] = useState<ContestDurationType | ContestDuration | undefined>(companyDefaultSettings?.shift_contest_end_variant || undefined);
  const [_, setContestStart] = useState();

  useEffect(() => {
    // Since the default settings can be undefined, and they are lazily loaded, we need to react if they are
    // loaded late. They should be loaded at this time, but we are using useEffect is just to be sure.
    contestEndVariant.setValue(companyDefaultSettings?.shift_contest_end_variant || undefined);
    contestEndTime.setValue(toHHMM(companyDefaultSettings?.shift_contest_end_time || undefined));
    setContestEndVariantValue(() => companyDefaultSettings?.shift_contest_end_variant || undefined);
  }, [companyDefaultSettings]);

  return (
    <div className="flex flex-col gap-2 items-center z-0">
      <div className="flex gap-2 items-center w-full relative">
        <ImageIcon name="remove_red_eye" className="size-5 shrink-0 mt-4" />
        <TyDatePicker
          name="contestStart"
          picker="date"
          onChange={(value) => {
            contestEndTime.setValue(undefined);
            setContestStart(value);
          }}
          showTime
          format="DD.MM.YYYY, HH:mm"
          label="Zveřejnění směn od"
          validate={(value) =>
            value?.isAfter(startDateTime) ? 'Datum zveřejnění směny musí být před začátkem nejstarší směny' : undefined
          }
        />
        {contestStart.getValue() ? (
          <div
            className="absolute right-4 top-8 cursor-pointer z-10"
            onClick={() => {
              contestStart.reset();
              setContestStart(undefined);
            }}
          >
            <FavouriteIcon name="close" className="size-4" />
          </div>
        ) : null}
      </div>

      <div className="flex items-start w-full pl-[28px]">
        <div className="w-[260px] relative">
          <TySelectInput
            disabled={!!companyDefaultSettings?.shift_contest_end_variant}
            initialValue={companyDefaultSettings?.shift_contest_end_variant}
            name="contestEndVariant"
            label="Zveřejnění směny do"
            optionsPosition="top"
            onChange={(value) => {
              contestEndTime.setValue(undefined);
              setContestEndVariantValue(value?.value || undefined);
            }}
          >
            <TyOption value={ContestDurationType.hoursBeforeAt} label="Hodin před nástupem">
              Hodin před nástupem
            </TyOption>
            <TyOption value={ContestDuration.DAY_OF_SHIFT_BEFORE_AT} label="V den nástupu v">
              V den nástupu v
            </TyOption>
            <TyOption value={ContestDuration.DAY_BEFORE_AT} label="Den před nástupem v">
              Den před nástupem v
            </TyOption>
            <TyOption value={ContestDuration.WORKDAY_BEFORE_AT} label="Pracovní den (včetně svátků) před nástupem v">
              Pracovní den (včetně svátků) před nástupem v
            </TyOption>
            <TyOption value={ContestDuration.NON_HOLIDAY_WORKDAY_BEFORE_AT} label="Pracovní den (mimo svátky) před nástupem v">
              Pracovní den (mimo svátky) před nástupem v
            </TyOption>
          </TySelectInput>
          {contestEndVariant.getValue() !== undefined && !companyDefaultSettings?.shift_contest_end_variant ? (
            <div
              className="absolute right-7 top-8 cursor-pointer z-10"
              onClick={() => {
                contestEndVariant.setValue(undefined);
                contestEndTime.reset();
              }}
            >
              <FavouriteIcon name="close" className="size-4" />
            </div>
          ) : null}
        </div>

        <div className="w-[100px] ml-auto">
          {contestEndVariantValue === ContestDurationType.hoursBeforeAt ? (
            <TySelectInput
              disabled={!contestEndVariantValue || !!companyDefaultSettings?.shift_contest_end_variant}
              initialValue={companyDefaultSettings?.shift_contest_end_time}
              name="contestEndTime"
              optionsPosition="top"
              className='mt-4'
              validate={(value) => validateContestEndTime(value, contestEndVariantValue)}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <TyOption key={`contestEndTime_${i}h`} value={i + 1} label={`${i + 1}h`}>
                  {i + 1}h
                </TyOption>
              ))}
            </TySelectInput>
          ) : (
            <TySelectInput<string | undefined>
              disabled={!contestEndVariantValue || !!companyDefaultSettings?.shift_contest_end_time}
              name="contestEndTime"
              optionsPosition="top"
              className='mt-4'
              validate={(value) => validateContestEndTime(value, contestEndVariantValue)}
            >
              {Array.from({ length: 48 }, (_, i) => {
                const hours = Math.floor(i / 2);
                const minutes = (i % 2) * 30;
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                return (
                  <TyOption key={timeString} value={timeString} label={timeString}>
                    {timeString}
                  </TyOption>
                );
              })}
            </TySelectInput>
          )}
        </div>
      </div>
    </div>
  );
};
